/**
 * Staff calendar catalog + calendar test API wrappers (DNKADM-0384 U2/U3; dink-api
 * DNKAPI-0538 U2/U3 as reshaped by DNKAPI-0543 U4).
 *
 * The per-coach routing FIELDS (`calendarId`, `calendarSyncEnabled`) ride the staff
 * entity GET/PUT in `./staff`; this module owns the two sub-resources beside them.
 * Bearer-only `/v2/admin/**` surface — `request()` routes it (DNKADM-0049
 * `isV2Surface`): bearer token, NO CSRF, NO cookies, the bounded 401→refresh→retry;
 * the mutating-audit `X-Session-Mode` stamp fires on the POST/DELETE.
 *
 * **Endpoints:**
 *  - `GET /v2/admin/staff/{id}/calendars` → {@link StaffCalendarCatalog}: the
 *    calendars the tenant's ACTIVE provider offers for this staff member, as
 *    `{ id, name }` pairs, or an empty list with an operator-facing reason (provider
 *    off, not connected, unreachable) — never a failure and never a partial list.
 *    `provider` is the tenant's current setting, so the picker can say "no provider"
 *    without a second read of tenant settings.
 *  - `POST /v2/admin/staff/{id}/calendar-test` → {@link StaffCalendarTestResult}:
 *    runs the real booking path once against the coach's configured calendar — the
 *    owner is verified, a 15-minute test appointment is created and then deleted with
 *    the cancellation call — and reports every step. Refused with a distinct `409`
 *    code per missing precondition (`STAFF_CALENDAR_TEST_PROVIDER_NONE` /
 *    `_NO_CALENDAR` / `_SYNC_OFF` / `_PROVIDER_UNAVAILABLE`), `502
 *    STAFF_CALENDAR_TEST_FAILED` when the provider was reached and failed. ADMIN
 *    only — never a STAFF session (this writes to a real person's calendar).
 *  - `DELETE /v2/admin/staff/{id}/calendar-test/{externalEventId}?calendarId=&kind=`
 *    → removes a test appointment the automatic delete could not (the POST answers
 *    `remove` only then). An event already gone is a success (`204`).
 *    `409 STAFF_CALENDAR_TEST_NOT_A_TEST_EVENT` refuses a real booking's mirror.
 *
 * Every read is parsed DEFENSIVELY (mirrors `staff.ts`): a calendar without a usable
 * id or name is dropped; an unknown outcome degrades to the server's own sentence.
 * The ids here (calendar, event, owner) are OPAQUE routing keys — DATA for the
 * write-back and the remove call, never rendered anywhere (spec Non-goals).
 */
import { request } from './client';
import {
  normalizeStaffCalendarProvider,
  type StaffCalendarProvider,
} from '@/utils/whitelabel';

/** v2 admin staff base path (bearer surface). */
const PATH = '/v2/admin/staff';

/** One calendar the active provider offers: the opaque id to store, the name to show. */
export interface ProviderCalendar {
  id: string;
  name: string;
}

/**
 * `GET /v2/admin/staff/{id}/calendars`. Exactly the server's invariant on the wire:
 * `calendars` OR `unavailableReason`, never both. An empty list with a `null` reason
 * means the provider genuinely offers no calendars.
 */
export interface StaffCalendarCatalog {
  /** The tenant's current provider setting — `'none'` when off. */
  provider: StaffCalendarProvider;
  calendars: ProviderCalendar[];
  /** Why the list is empty, in the server's (brand-scrubbed at render) words, or `null`. */
  unavailableReason: string | null;
}

/**
 * The three ways a test ends (DNKAPI-0543 U4 wire values), kept open as `string` so an
 * unknown future outcome degrades to the server's sentence rather than a crash:
 *  - `created_and_deleted` — the whole booking path works;
 *  - `created_delete_failed` — the appointment is still on the calendar; `remove` takes
 *    it back;
 *  - `verified_no_contact` — calendar and owner verified, but the acting admin has no
 *    contact record to book with, so nothing was created.
 */
export type StaffCalendarTestOutcome =
  | 'created_and_deleted'
  | 'created_delete_failed'
  | 'verified_no_contact'
  | string;

/**
 * What the explicit remove needs (DNKAPI-0538 U3): the server rebuilds the provider
 * ref from exactly these three, nothing is persisted. All opaque; none rendered.
 */
export interface StaffCalendarTestRemoval {
  externalEventId: string;
  calendarId: string;
  kind: string;
}

/** The parsed `POST …/calendar-test` report. */
export interface StaffCalendarTestResult {
  outcome: StaffCalendarTestOutcome;
  /** The server's one operator-readable sentence (scrub at render). */
  message: string;
  /** The calendar owner's best-effort NAME as the provider records it, or `null`. Never the id. */
  ownerName: string | null;
  /**
   * The explicit remove action, present ONLY when the appointment was created but the
   * automatic delete failed. `null` otherwise (nothing is left on the calendar).
   */
  remove: StaffCalendarTestRemoval | null;
}

// --- Defensive parsing helpers (mirrors staff.ts) ------------------------------

function isObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

/** A trimmed non-empty string, or null. */
function asText(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed.length === 0 ? null : trimmed;
}

/** Parse one raw calendar, or null without a usable id (the name falls back to nothing renderable → dropped). */
function parseCalendar(raw: unknown): ProviderCalendar | null {
  if (!isObject(raw)) return null;
  const id = asText(raw['id']);
  const name = asText(raw['name']);
  // The server already falls a blank name back to the id; the console never shows an
  // id, so a row with no name at all is not offerable and is dropped.
  if (id === null || name === null) return null;
  return { id, name };
}

function parseCatalog(raw: unknown): StaffCalendarCatalog {
  const o = isObject(raw) ? raw : {};
  const calendars = Array.isArray(o['calendars'])
    ? o['calendars'].map(parseCalendar).filter((c): c is ProviderCalendar => c !== null)
    : [];
  return {
    provider: normalizeStaffCalendarProvider(o['provider']),
    calendars,
    unavailableReason: asText(o['unavailableReason']),
  };
}

/**
 * Parse the test report. The remove action is built from the report's own fields —
 * the appointment's `externalEventId` + `kind` and the report's `calendarId` — and only
 * when the server answered a `remove` action (delete-failed). A report that says
 * "remove" but lacks any of the three cannot be acted on and is treated as none.
 */
function parseTestResult(raw: unknown): StaffCalendarTestResult {
  const o = isObject(raw) ? raw : {};
  const owner = isObject(o['owner']) ? o['owner'] : {};
  const appointment = isObject(o['appointment']) ? o['appointment'] : {};
  let remove: StaffCalendarTestRemoval | null = null;
  if (isObject(o['remove'])) {
    const externalEventId = asText(appointment['externalEventId']);
    const kind = asText(appointment['kind']);
    const calendarId = asText(o['calendarId']);
    if (externalEventId !== null && kind !== null && calendarId !== null) {
      remove = { externalEventId, calendarId, kind };
    }
  }
  return {
    outcome: asText(o['outcome']) ?? '',
    message: asText(o['message']) ?? '',
    ownerName: asText(owner['name']),
    remove,
  };
}

// --- Path helpers --------------------------------------------------------------

function staffPath(id: string): string {
  return `${PATH}/${encodeURIComponent(id)}`;
}

// --- Endpoints -----------------------------------------------------------------

/**
 * `GET /v2/admin/staff/{id}/calendars` → the calendars the active provider offers for
 * this staff member, or an empty list with a reason. Throws a typed `ApiError` only on
 * a non-2xx (`404` for a foreign staff id; the provider being off/unreachable is a
 * `200` with a reason, by the server's contract).
 */
export async function getStaffCalendars(id: string): Promise<StaffCalendarCatalog> {
  const body = await request<unknown>('GET', `${staffPath(id)}/calendars`);
  return parseCatalog(body);
}

/**
 * `POST /v2/admin/staff/{id}/calendar-test` → run the calendar test once and report.
 * Throws a typed `ApiError` on a refusal (`409` + code), a provider failure (`502`),
 * or a foreign id (`404`) — map the code with `staffCalendarTestErrorMessage`.
 */
export async function sendStaffCalendarTest(id: string): Promise<StaffCalendarTestResult> {
  const body = await request<unknown>('POST', `${staffPath(id)}/calendar-test`);
  return parseTestResult(body);
}

/**
 * `DELETE /v2/admin/staff/{id}/calendar-test/{externalEventId}?calendarId=&kind=` →
 * remove a test appointment the automatic delete could not. `204`; an event already
 * gone is a success. Throws a typed `ApiError` (`409 STAFF_CALENDAR_TEST_NOT_A_TEST_EVENT`
 * for a real booking's mirror, `502` when the provider fails).
 */
export async function removeStaffCalendarTestEvent(
  id: string,
  removal: StaffCalendarTestRemoval,
): Promise<void> {
  await request<unknown>(
    'DELETE',
    `${staffPath(id)}/calendar-test/${encodeURIComponent(removal.externalEventId)}`,
    { query: { calendarId: removal.calendarId, kind: removal.kind } },
  );
}
